import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Crown, Trophy } from "lucide-react";
import SeoHead from "../components/SeoHead";

const TIER_LABEL = { advocate: "Advocate", ambassador: "Ambassador", elite: "Elite", regional: "Regional" };

export default function AmbassadorLeaderboard() {
  const [period, setPeriod] = useState("30d");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/ambassador/leaderboard?period=${period}&limit=50`);
        if (!cancelled) setRows(r.data.rows);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="leaderboard-page">
      <SeoHead
        title="Leaderboard — Cauldron Ambassador Program"
        description="See the top Cauldron Ambassadors growing the spiritual marketplace."
      />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/ambassadors" className="inline-flex items-center gap-2 text-foreground/65 hover:text-secondary text-xs uppercase tracking-wider mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to program
        </Link>
        <div className="label-mystic text-secondary mb-3 flex items-center gap-2">
          <Trophy className="w-4 h-4" /> Ambassador Leaderboard
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl">Who&apos;s growing the Cauldron.</h1>

        <div className="mt-10 flex gap-2 border-b border-secondary/15 pb-px">
          {[["30d", "Last 30 days"], ["all", "All time"]].map(([k, label]) => (
            <button key={k} onClick={() => setPeriod(k)}
              className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] transition ${
                period === k ? "text-secondary border-b-2 border-secondary -mb-px" : "text-foreground/55 hover:text-foreground/85"
              }`}
              data-testid={`board-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8 parchment p-2 sm:p-4" data-testid="leaderboard-table">
          {loading && <div className="p-6 text-foreground/60 text-sm">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-12 text-center">
              <Trophy className="w-8 h-8 text-secondary mx-auto opacity-60" />
              <p className="text-sm text-foreground/65 mt-4">
                The leaderboard is empty for this period.
                <br />
                <Link to="/ambassadors/apply" className="text-secondary hover:underline">Be the first to claim a spot →</Link>
              </p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <div className="divide-y divide-foreground/5">
              {rows.map((r) => (
                <div key={r.userId} className="flex items-center gap-4 p-3 sm:p-4 hover:bg-secondary/[0.03] transition" data-testid={`board-row-${r.rank}`}>
                  <div className={`font-serif text-2xl w-12 text-center ${r.rank <= 3 ? "text-secondary" : "text-foreground/55"}`}>
                    {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
                  </div>
                  {r.imageUrl ? (
                    <img src={r.imageUrl} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center text-secondary font-serif">{r.name?.[0] || "?"}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-base flex items-center gap-1.5 truncate">
                      {r.name}
                      {r.isFounding && <Crown className="w-3.5 h-3.5 text-secondary flex-shrink-0" title="Founding Ambassador" />}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-foreground/55">{TIER_LABEL[r.tier] || r.tier}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-lg text-secondary">${r.usd.toFixed(0)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-foreground/55">{r.subs} subs</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 text-center">
          <Link to="/ambassadors/apply"
            className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/85 px-8 py-4 rounded-sm uppercase tracking-[0.2em] text-xs font-semibold">
            Join the leaderboard →
          </Link>
        </div>
      </div>
    </div>
  );
}
